import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { COMPANY_INFO } from "../data/siteData";
import ContactForm from "../components/ContactForm";

export default function ContactPage() {
  return (
    <>
      <Helmet>
        <title>Kontakt NF-Polering | Få et Tilbud på Vinduespudsning & Fliserens</title>
        <meta
          name="description"
          content="Kontakt NF-Polering på tlf. 30 14 60 70 eller udfyld formularen. Få et uforpligtende og gennemsigtigt tilbud på vinduespudsning eller fliserens på Vestsjælland."
        />
        <link rel="canonical" href="https://nf-polering.dk/kontakt" />
        <meta property="og:title" content="Kontakt NF-Polering | Få et Tilbud på Vinduespudsning & Fliserens" />
        <meta
          property="og:description"
          content="Vi sidder klar til at besvare dine henvendelser hurtigt. Ring til os på 30 14 60 70 eller send en besked via vores kontaktformular."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/hero-vinduespudsning-oversigt.png"
        />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-900 border-b border-slate-800 text-white pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1811_856af0cf.jpg"
            alt="NF-Polering kontakt"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-5">
            <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
              Hele Vestsjælland
            </span>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight uppercase leading-tight">
              Kontakt NF-Polering
            </h1>

            <p className="text-lg sm:text-xl text-slate-200 leading-relaxed font-normal">
              Har du spørgsmål til vores ydelser, eller ønsker du et uforpligtende tilbud på vinduespudsning eller fliserens? Vi sidder klar til at hjælpe dig på hele Vestsjælland.
            </p>

            <div className="pt-3 flex flex-wrap items-center gap-4">
              <a
                href={COMPANY_INFO.phoneTel}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-base transition-transform duration-200 hover:-translate-y-1 whitespace-nowrap shadow-lg shadow-cyan-500/20"
              >
                <i className="fa-solid fa-phone text-slate-950" aria-hidden="true" />
                <span>Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white bg-white/10 hover:bg-white/20 font-bold text-base backdrop-blur-sm transition-transform duration-200 hover:-translate-y-1 whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-cyan-300" aria-hidden="true" />
                <span>Send en email</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Contact Info Grid */}
      <section className="py-12 bg-[#113151] text-white border-b border-cyan-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-cyan-400 text-xl shrink-0" aria-hidden="true" />
                <h3 className="font-bold text-white text-base uppercase tracking-wide">
                  Telefon / SMS
                </h3>
              </div>
              <p className="text-sm text-slate-300">Ring eller send en SMS til os:</p>
              <a
                href={COMPANY_INFO.phoneTel}
                className="block text-base font-bold text-white hover:text-cyan-300 transition-colors"
              >
                {COMPANY_INFO.phone}
              </a>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-cyan-400 text-xl shrink-0" aria-hidden="true" />
                <h3 className="font-bold text-white text-base uppercase tracking-wide">
                  Email
                </h3>
              </div>
              <p className="text-sm text-slate-300">Send os en besked døgnet rundt:</p>
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="block text-base font-bold text-white hover:text-cyan-300 transition-colors break-all"
              >
                {COMPANY_INFO.email}
              </a>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-location-dot text-cyan-400 text-xl shrink-0" aria-hidden="true" />
                <h3 className="font-bold text-white text-base uppercase tracking-wide">
                  Adresse & Base
                </h3>
              </div>
              <p className="text-sm text-slate-300">Vores base er i Slagelse:</p>
              <p className="text-base font-bold text-white">
                {COMPANY_INFO.address}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-clock text-cyan-400 text-xl shrink-0" aria-hidden="true" />
                <h3 className="font-bold text-white text-base uppercase tracking-wide">
                  Åbningstider & Svartid
                </h3>
              </div>
              <p className="text-sm text-slate-300">Mandag – Fredag: 08:00 – 16:00</p>
              <p className="text-sm text-slate-300">Lørdag – Søndag: 10:00 – 14:00 (På telefon)</p>
              <p className="text-xs text-slate-400">
                Hurtig tilbagemelding på SMS & mail inden for få timer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Form & Info Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Col: Info & Dækningsområder */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 uppercase tracking-tight">
                  Få et hurtigt prisoverslag
                </h2>
                <p className="text-slate-700 leading-relaxed text-base">
                  Udfyld formularen med dine oplysninger og adresse. Vi vurderer din opgave og vender hurtigt tilbage med en fast, gennemsigtig pris uden skjulte gebyrer.
                </p>
              </div>

              <div className="bg-white p-6 border border-slate-200 space-y-4">
                <h3 className="font-bold text-slate-950 text-base uppercase">
                  Sådan foregår det:
                </h3>
                <ul className="space-y-3 text-sm text-slate-700">
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                      1
                    </span>
                    <span>Du udfylder formularen med adresse og ønsker.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                      2
                    </span>
                    <span>Vi beregner din faste pris og kontakter dig hurtigt.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                      3
                    </span>
                    <span>Vi aftaler dato og pudser dine ruder til perfektion.</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white border border-slate-200 space-y-3">
                <h3 className="font-bold text-slate-950 text-base uppercase">
                  Dækningsområder på Vestsjælland
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Vi dækker bl.a. Slagelse, Sorø, Skælskør, Korsør, Ringsted, Næstved, Dalmose, Høng, Fuglebjerg og Karrebæksminde.
                </p>
                <Link
                  to="/daekningsomraader"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-950 hover:text-cyan-700 transition-colors pt-1"
                >
                  <span>Se alle 9 byer og postnumre</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Col: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm
                id="tilbud"
                title="Udfyld formularen og få et tilbud"
                subtitle="Vi svarer hurtigt med en skarp pris uden skjulte gebyrer."
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
